import { Icon, LabeledCheckbox, Surface, Text } from "@zapier/design-system";

import { Button } from "@zapier/design-system-beta";
import styles from "./AiPromptBuilder.module.css";
import type { PromptPlan } from "@zapier/types";
import { useState } from "react";
import { emitUserInteractionEventAtom } from "@zapier/canvas-state";
import { useSetAtom } from "jotai";

interface AiPromptBuilderProps {
  plan: PromptPlan | null;
  isStreaming: boolean;
  handleSubmit: (prompt: string) => void;
}

const getIcon = (type: string) => {
  if (type === "table") return "navTables";
  if (type === "interface") return "navInterfaces";
  if (type === "zap") return "miscBoltAltFill";

  return "miscAIFill";
};

export function AiPromptBuilder({
  handleSubmit,
  isStreaming,
  plan,
}: AiPromptBuilderProps) {
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventAtom);
  const [building, setBuilding] = useState(false);
  const [rejectedComponents, setRejectedComponents] = useState<string[]>([]);
  const [additionalComponents, setAdditionalComponents] = useState<string[]>(
    [],
  );

  if (!plan) return null;

  const handleBuild = async () => {
    setBuilding(true);
    let prompt = `Here is the initial plan we proposed to the user: ${plan?.prompt}`;

    if (rejectedComponents.length > 0) {
      prompt += `\n\nHere are the components the user does not want to be a part of the system: ${rejectedComponents.join(
        ", ",
      )}`;
    }

    if (additionalComponents.length > 0) {
      prompt += `\n\nHere are the additional components the user requested to be a part of the system: ${additionalComponents.join(
        ", ",
      )}`;
    }

    handleSubmit(prompt);

    await emitUserInteractionEvent({
      eventAction: "click",
      interactionGoal: "Built with an enriched prompt",
    });
  };

  return (
    <div className={styles.container}>
      {plan?.planComponents?.length > 0 && (
        <div className={styles.nextStepsSection}>
          <div className={styles.planText}>
            <Text type="ParagraphHeader3">Include in your system</Text>
          </div>
          <Surface>
            {plan?.planComponents?.map((component, index) => (
              <div
                key={`${component.type}-${index}`}
                className={styles.planComponent}
              >
                <Surface
                  key={`${component.type}-${index}`}
                  borderColor="StrokeDefault"
                >
                  <LabeledCheckbox
                    checked={
                      !rejectedComponents.includes(component.description)
                    }
                    onChange={() => {
                      if (rejectedComponents.includes(component.description)) {
                        setRejectedComponents(
                          rejectedComponents.filter(
                            (c) => c !== component.description,
                          ),
                        );
                      } else {
                        setRejectedComponents([
                          ...rejectedComponents,
                          component.description,
                        ]);
                      }
                    }}
                  >
                    <div className={styles.systemComponentType}>
                      <Icon
                        color="BrandOrange"
                        name={getIcon(component?.type)}
                      />
                      <Text>{component.description}</Text>
                    </div>
                  </LabeledCheckbox>
                </Surface>
              </div>
            ))}
          </Surface>
        </div>
      )}

      {plan?.nextSteps?.length > 0 && (
        <div className={styles.nextStepsSection}>
          <div className={styles.planText}>
            <Text type="ParagraphHeader3">Additional steps to consider</Text>
          </div>
          {plan?.nextSteps?.map((step, index) => (
            <div key={`${step}-${index}`} className={styles.planComponent}>
              <Surface borderColor="StrokeDefault">
                <LabeledCheckbox
                  checked={additionalComponents.includes(step)}
                  onChange={() => {
                    if (additionalComponents.includes(step)) {
                      setAdditionalComponents(
                        additionalComponents.filter(
                          (component) => component !== step,
                        ),
                      );
                    } else {
                      setAdditionalComponents([...additionalComponents, step]);
                    }
                  }}
                >
                  <Text
                    color={
                      additionalComponents.includes(step)
                        ? "TextDefault"
                        : "TextWeaker"
                    }
                    type={
                      additionalComponents.includes(step) ? "Body2" : "Body1"
                    }
                  >
                    {step}
                  </Text>
                </LabeledCheckbox>
              </Surface>
            </div>
          ))}
        </div>
      )}

      {!isStreaming && (
        <div className={styles.buttonWrapper}>
          <Button
            size="large"
            variant="primary"
            onPress={handleBuild}
            isLoading={building}
          >
            Start building
          </Button>
        </div>
      )}
    </div>
  );
}
